"use client";

import { c as _c } from "react/compiler-runtime";
import { forwardRef } from "react";
import { Flame, LayoutGrid, List, Star, Trophy } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import { AppliedFilters, FacetPopover, SearchField, TabSwitch, ViewToggle, type AppliedChip } from "@/features/home-v2/browse/BrowseControls";
import SortMenu from "@/features/home-v2/browse/SortMenu";
import type { CollectionsListing, CollectionTab, CollectionView } from "./useCollectionsListing";

/**
 * The /collection quick bar and tab row, assembled from the v2 browse kit.
 * Every control reads and writes the listing hook — no state of its own.
 */
const CollectionsToolbar = forwardRef<HTMLElement, {
  listing: CollectionsListing;
  dict: Dictionary;
  lang: "fa" | "en";
}>(function CollectionsToolbar(t0, barRef) {
  const $ = _c(123);
  const {
    listing,
    dict,
    lang
  } = t0;
  const t = dict.browse;
  const isFa = lang === "fa";
  const t1 = isFa ? "fa-IR" : "en-US";
  let t2;
  if ($[0] !== t1) {
    t2 = new Intl.NumberFormat(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const num = t2;
  let t3;
  if ($[2] !== listing.network.options || $[3] !== listing.network.value) {
    t3 = listing.network.options.find(o => o.value === listing.network.value)?.label;
    $[2] = listing.network.options;
    $[3] = listing.network.value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const networkLabel = t3;
  let t4;
  if ($[5] !== listing.category.options || $[6] !== listing.category.value) {
    t4 = listing.category.options.find(o_0 => o_0.value === listing.category.value)?.label;
    $[5] = listing.category.options;
    $[6] = listing.category.value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const categoryLabel = t4;
  let t5;
  if ($[8] !== listing) {
    t5 = listing.q ? [{
      key: "q",
      label: listing.q,
      onRemove: () => listing.setQuery("")
    }] : [];
    $[8] = listing;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== categoryLabel || $[11] !== listing) {
    t6 = categoryLabel ? [{
      key: "category",
      label: categoryLabel,
      onRemove: () => listing.setCategory(null)
    }] : [];
    $[10] = categoryLabel;
    $[11] = listing;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== listing || $[14] !== networkLabel) {
    t7 = networkLabel ? [{
      key: "network",
      label: networkLabel,
      onRemove: () => listing.setNetwork(null)
    }] : [];
    $[13] = listing;
    $[14] = networkLabel;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = [...t5, ...t6, ...t7];
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const chips = t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Trophy aria-hidden={true} />;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t.tabTop) {
    t10 = {
      value: "top",
      label: t.tabTop,
      icon: t9
    };
    $[21] = t.tabTop;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Flame aria-hidden={true} />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t.tabTrending) {
    t12 = {
      value: "trending",
      label: t.tabTrending,
      icon: t11
    };
    $[24] = t.tabTrending;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Star aria-hidden={true} />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t.tabWatchlist) {
    t14 = {
      value: "watchlist",
      label: t.tabWatchlist,
      icon: t13
    };
    $[27] = t.tabWatchlist;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t14) {
    t15 = [t10, t12, t14];
    $[29] = t10;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const tabs = t15;
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LayoutGrid aria-hidden={true} />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t.viewGrid) {
    t17 = {
      value: "grid",
      label: t.viewGrid,
      icon: t16
    };
    $[34] = t.viewGrid;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <List aria-hidden={true} />;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t.viewTable) {
    t19 = {
      value: "table",
      label: t.viewTable,
      icon: t18
    };
    $[37] = t.viewTable;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t17 || $[40] !== t19) {
    t20 = [t17, t19];
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const views = t20;
  let t21;
  if ($[42] !== t.sortNewest) {
    t21 = {
      key: "newest",
      label: t.sortNewest
    };
    $[42] = t.sortNewest;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t.sortOldest) {
    t22 = {
      key: "oldest",
      label: t.sortOldest
    };
    $[44] = t.sortOldest;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t.sortNameAsc) {
    t23 = {
      key: "name-asc",
      label: t.sortNameAsc
    };
    $[46] = t.sortNameAsc;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t.sortNameDesc) {
    t24 = {
      key: "name-desc",
      label: t.sortNameDesc
    };
    $[48] = t.sortNameDesc;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t.sortCreatedNewest) {
    t25 = {
      key: "created-desc",
      label: t.sortCreatedNewest
    };
    $[50] = t.sortCreatedNewest;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t.sortCreatedOldest) {
    t26 = {
      key: "created-asc",
      label: t.sortCreatedOldest
    };
    $[52] = t.sortCreatedOldest;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== t24 || $[58] !== t25 || $[59] !== t26) {
    t27 = [t21, t22, t23, t24, t25, t26];
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const sorts = t27 as const;
  const t28 = isFa ? "\u0627\u0628\u0632\u0627\u0631 \u0645\u0631\u0648\u0631 \u06A9\u0627\u0644\u06A9\u0634\u0646\u200C\u0647\u0627" : "Collection browsing controls";
  let t29;
  if ($[61] !== listing.q || $[62] !== listing.setQuery || $[63] !== t.clearSearch || $[64] !== t.searchCollections) {
    t29 = <SearchField id="clx-search" value={listing.q} onChange={listing.setQuery} placeholder={t.searchCollections} label={t.searchCollections} clearLabel={t.clearSearch} />;
    $[61] = listing.q;
    $[62] = listing.setQuery;
    $[63] = t.clearSearch;
    $[64] = t.searchCollections;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== listing.setSort || $[67] !== listing.sort || $[68] !== sorts || $[69] !== t.sortLabel) {
    t30 = <SortMenu value={listing.sort} options={sorts} onChange={listing.setSort} label={t.sortLabel} />;
    $[66] = listing.setSort;
    $[67] = listing.sort;
    $[68] = sorts;
    $[69] = t.sortLabel;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  const t31 = (listing.network.value ? 1 : 0) + (listing.category.value ? 1 : 0);
  let t32;
  if ($[71] !== num) {
    t32 = n => num.format(n);
    $[71] = num;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== listing.category.options || $[74] !== listing.category.value || $[75] !== listing.setCategory || $[76] !== t.allCategories || $[77] !== t.category) {
    t33 = {
      id: "category",
      label: t.category,
      allLabel: t.allCategories,
      options: listing.category.options,
      value: listing.category.value,
      onChange: listing.setCategory
    };
    $[73] = listing.category.options;
    $[74] = listing.category.value;
    $[75] = listing.setCategory;
    $[76] = t.allCategories;
    $[77] = t.category;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== listing.network.options || $[80] !== listing.network.value || $[81] !== listing.setNetwork || $[82] !== t.allNetworks || $[83] !== t.network) {
    t34 = {
      id: "network",
      label: t.network,
      allLabel: t.allNetworks,
      options: listing.network.options,
      value: listing.network.value,
      onChange: listing.setNetwork
    };
    $[79] = listing.network.options;
    $[80] = listing.network.value;
    $[81] = listing.setNetwork;
    $[82] = t.allNetworks;
    $[83] = t.network;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== t33 || $[86] !== t34) {
    t35 = [t33, t34];
    $[85] = t33;
    $[86] = t34;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t.filters || $[89] !== t31 || $[90] !== t32 || $[91] !== t35) {
    t36 = <FacetPopover label={t.filters} activeCount={t31} formatCount={t32} groups={t35} />;
    $[88] = t.filters;
    $[89] = t31;
    $[90] = t32;
    $[91] = t35;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  const t37 = isFa ? "\u0646\u0648\u0639 \u0646\u0645\u0627\u06CC\u0634" : "View";
  let t38;
  if ($[93] !== listing.setView || $[94] !== listing.view || $[95] !== t37 || $[96] !== views) {
    t38 = <ViewToggle value={listing.view} options={views} onChange={listing.setView} label={t37} />;
    $[93] = listing.setView;
    $[94] = listing.view;
    $[95] = t37;
    $[96] = views;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== t30 || $[99] !== t36 || $[100] !== t38) {
    t39 = <div className="v2-clx-bar-controls">{t30}{t36}{t38}</div>;
    $[98] = t30;
    $[99] = t36;
    $[100] = t38;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== barRef || $[103] !== t28 || $[104] !== t29 || $[105] !== t39) {
    t40 = <section ref={barRef} className="v2-clx-bar" aria-label={t28}>{t29}{t39}</section>;
    $[102] = barRef;
    $[103] = t28;
    $[104] = t29;
    $[105] = t39;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  let t41;
  if ($[107] !== dict.sidebar.collection || $[108] !== listing.setTab || $[109] !== listing.tab || $[110] !== tabs) {
    t41 = <TabSwitch value={listing.tab} options={tabs} onChange={listing.setTab} label={dict.sidebar.collection} />;
    $[107] = dict.sidebar.collection;
    $[108] = listing.setTab;
    $[109] = listing.tab;
    $[110] = tabs;
    $[111] = t41;
  } else {
    t41 = $[111];
  }
  let t42;
  if ($[112] !== chips || $[113] !== listing.clearAllKeepTab || $[114] !== t.appliedFilters || $[115] !== t.clearAll) {
    t42 = <AppliedFilters chips={chips} label={t.appliedFilters} clearLabel={t.clearAll} onClear={listing.clearAllKeepTab} />;
    $[112] = chips;
    $[113] = listing.clearAllKeepTab;
    $[114] = t.appliedFilters;
    $[115] = t.clearAll;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== t41 || $[118] !== t42) {
    t43 = <div className="v2-clx-tabs-row">{t41}{t42}</div>;
    $[117] = t41;
    $[118] = t42;
    $[119] = t43;
  } else {
    t43 = $[119];
  }
  let t44;
  if ($[120] !== t40 || $[121] !== t43) {
    t44 = <>{t40}{t43}</>;
    $[120] = t40;
    $[121] = t43;
    $[122] = t44;
  } else {
    t44 = $[122];
  }
  return t44;
});
export default CollectionsToolbar;